import { useState } from 'react';
import { AlertTriangle, CalendarClock, RefreshCw } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tooltip } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import type {
  MatchProposal,
  RoundScheduleSlot,
} from '@/types/tournament-round.model';
import { ForcedReasonBadge } from '@/components/tournament/fixture/match/ForcedReasonBadge';

interface ProposalGridProps {
  proposals: MatchProposal[];
  readOnly?: boolean;
  /** Resolves to whether the match was rescheduled; the row closes its panel only on success. */
  onReschedule?: (matchId: string, startTime: string) => Promise<boolean>;
  /** The round's week ('yyyy-MM-dd'): the backend only accepts a new time inside it. */
  week?: { start: string; end: string };
}

/**
 * Match proposal grid for a round.
 * Highlights conflicts in red and distinguishes the make-up ones.
 */
export function ProposalGrid({ proposals, readOnly = false, onReschedule, week }: ProposalGridProps) {
  if (proposals.length === 0) {
    return (
      <p className="py-4 text-center text-sm text-muted-foreground">
        No hay propuesta generada todavía.
      </p>
    );
  }

  const hasRealConflicts = proposals.some(
    (p) => p.conflict && !p.conflictMissingAvailability,
  );
  const hasMissingAvailability = proposals.some(
    (p) => p.conflict && p.conflictMissingAvailability,
  );
  const hasOverlapWarnings = proposals.some((p) => p.overlapWarning && !p.conflict);

  return (
    <div className="space-y-3">
      {hasRealConflicts && (
        <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          <span>
            Hay partidos con horario forzado porque la disponibilidad de los equipos no
            coincidió. Revisalos y reprogramalos antes de publicar.
          </span>
        </div>
      )}
      {hasMissingAvailability && (
        <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-700">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          <span>
            Hay partidos donde un equipo no cargó disponibilidad y el sistema le asignó un
            horario. Revisalos por si querés ajustarlos.
          </span>
        </div>
      )}
      {!hasRealConflicts && !hasMissingAvailability && hasOverlapWarnings && (
        <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-700">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          <span>Se detectaron posibles solapes de horario en la propuesta. Revisala antes de publicar.</span>
        </div>
      )}

      <div className="space-y-2">
        {proposals.map((proposal) => (
          <ProposalRow
            key={proposal.matchId}
            proposal={proposal}
            readOnly={readOnly}
            onReschedule={onReschedule}
            week={week}
          />
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// PROPOSAL ROW
// ============================================================================

interface ProposalRowProps {
  proposal: MatchProposal;
  readOnly: boolean;
  /** Resolves to whether the match was rescheduled; the row closes its panel only on success. */
  onReschedule?: (matchId: string, startTime: string) => Promise<boolean>;
  /** The round's week ('yyyy-MM-dd'): the backend only accepts a new time inside it. */
  week?: { start: string; end: string };
}

function ProposalRow({ proposal, readOnly, onReschedule, week }: ProposalRowProps) {
  const [showReschedule, setShowReschedule] = useState(false);
  const [newDateTime, setNewDateTime] = useState('');
  const [isRescheduling, setIsRescheduling] = useState(false);

  // The input's min/max don't stop a typed value, so check the week here ('yyyy-MM-ddTHH:mm'
  // strings compare chronologically).
  const outOfWeek =
    Boolean(week && newDateTime) &&
    (newDateTime < `${week!.start}T00:00` || newDateTime > `${week!.end}T23:59`);

  const handleReschedule = async () => {
    if (!onReschedule || !newDateTime || outOfWeek) return;
    setIsRescheduling(true);
    try {
      // Convert datetime-local (YYYY-MM-DDTHH:mm) to the backend format (yyyy-MM-ddTHH:mm:ss)
      const startTime = newDateTime.length === 16 ? `${newDateTime}:00` : newDateTime;
      const rescheduled = await onReschedule(proposal.matchId, startTime);
      if (rescheduled) {
        setShowReschedule(false);
        setNewDateTime('');
      }
    } finally {
      setIsRescheduling(false);
    }
  };

  return (
    <Card
      className={cn(
        'transition-colors',
        proposal.conflict &&
          !proposal.conflictMissingAvailability &&
          'border-red-300 bg-red-50',
        proposal.conflict &&
          proposal.conflictMissingAvailability &&
          'border-amber-200 bg-amber-50',
        !proposal.conflict && proposal.overlapWarning && 'border-amber-200 bg-amber-50',
        proposal.recoverySuggestion &&
          !proposal.conflict &&
          !proposal.overlapWarning &&
          'border-orange-200 bg-orange-50',
      )}
    >
      <CardContent className="p-3">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-1 flex-col gap-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-medium text-gray-900">
                {proposal.team1Name} <span className="text-muted-foreground">vs</span> {proposal.team2Name}
              </span>
              {proposal.conflict && !proposal.conflictMissingAvailability && (
                <Tooltip content="La disponibilidad de los equipos no coincidió y el sistema forzó este horario. Te recomendamos reprogramarlo.">
                  <Badge variant="destructive" className="text-xs">
                    <AlertTriangle className="mr-1 h-3 w-3" />
                    Horario forzado
                  </Badge>
                </Tooltip>
              )}
              {proposal.conflict && proposal.conflictMissingAvailability && (
                <Tooltip content="Un equipo no cargó su disponibilidad, así que el sistema le asignó un horario. Podés reprogramarlo si hace falta.">
                  <Badge variant="outline" className="border-amber-300 bg-amber-50 text-xs text-amber-700">
                    <AlertTriangle className="mr-1 h-3 w-3" />
                    Falta disponibilidad
                  </Badge>
                </Tooltip>
              )}
              {!proposal.conflict && proposal.overlapWarning && (
                <Badge variant="outline" className="border-amber-300 bg-amber-50 text-xs text-amber-700">
                  <AlertTriangle className="mr-1 h-3 w-3" />
                  Posible solape
                </Badge>
              )}
              {/* Same mark as the shared grid: the slot has more matches than courts. */}
              <ForcedReasonBadge reason={proposal.forcedReason} />
              {proposal.scheduled === false && (
                <Badge variant="outline" className="border-slate-300 bg-slate-50 text-xs text-slate-700">
                  Propuesto
                </Badge>
              )}
              {proposal.pendingReschedule && (
                <Badge variant="outline" className="border-blue-300 bg-blue-50 text-xs text-blue-700">
                  <CalendarClock className="mr-1 h-3 w-3" />
                  Reprogramación pendiente
                </Badge>
              )}
              {proposal.recoverySuggestion && (
                <Badge variant="outline" className="border-orange-200 bg-orange-50 text-orange-700 text-xs">
                  Recuperación
                </Badge>
              )}
            </div>

            <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
              {proposal.clubName && <span>Club: {proposal.clubName}</span>}
              {proposal.date && proposal.startTime && (
                <span>
                  {new Date(proposal.date + 'T00:00:00').toLocaleDateString('es-AR', {
                    weekday: 'short',
                    day: 'numeric',
                    month: 'short',
                  })}{' '}
                  a las {proposal.startTime}
                </span>
              )}
              {(!proposal.date || !proposal.startTime) && (
                <span className="text-amber-600">Sin horario asignado</span>
              )}
            </div>
          </div>

          {/* Published rounds are read-only, except for matches that got no slot: publishing no longer
              confirms them, so rescheduling is the only way to give them a time. */}
          {(!readOnly || proposal.pendingReschedule) && (
            <Button
              variant="outline"
              size="sm"
              className="shrink-0"
              onClick={() => setShowReschedule(!showReschedule)}
            >
              <CalendarClock className="mr-1 h-4 w-4" />
              Reprogramar
            </Button>
          )}
        </div>

        {/* Reschedule panel */}
        {showReschedule && (
          <div className="mt-3 space-y-3 border-t pt-3">
            {/* Availability loaded per team: click a slot to fill in date/time */}
            <div className="grid gap-2 sm:grid-cols-2">
              <TeamAvailabilityHint
                name={proposal.team1Name}
                slots={proposal.team1Availability}
                onPick={(dt) => setNewDateTime(dt)}
              />
              <TeamAvailabilityHint
                name={proposal.team2Name}
                slots={proposal.team2Availability}
                onPick={(dt) => setNewDateTime(dt)}
              />
            </div>
            <div className="flex flex-wrap items-end gap-2">
            <div className="flex-1 space-y-1 min-w-[200px]">
              <Label htmlFor={`reschedule-${proposal.matchId}`} className="text-xs">
                Nueva fecha y hora
              </Label>
              <Input
                id={`reschedule-${proposal.matchId}`}
                type="datetime-local"
                value={newDateTime}
                onChange={(e) => setNewDateTime(e.target.value)}
                // Moving a match to another week means another round: that is done from that round.
                min={week ? `${week.start}T00:00` : undefined}
                max={week ? `${week.end}T23:59` : undefined}
                className="text-sm"
                aria-invalid={outOfWeek}
              />
              {outOfWeek && week && (
                <p className="text-xs text-red-600">
                  La fecha tiene que estar entre el {formatDayMonth(week.start)} y el {formatDayMonth(week.end)}.
                </p>
              )}
            </div>
            <Button
              size="sm"
              onClick={handleReschedule}
              disabled={!newDateTime || outOfWeek || isRescheduling}
            >
              {isRescheduling ? (
                <RefreshCw className="mr-1 h-4 w-4 animate-spin" />
              ) : (
                <CalendarClock className="mr-1 h-4 w-4" />
              )}
              Confirmar
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setShowReschedule(false);
                setNewDateTime('');
              }}
            >
              Cancelar
            </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/** 'yyyy-MM-dd' -> 'dd/MM'. */
function formatDayMonth(ymd: string): string {
  const [, month, day] = ymd.split('-');
  return `${day}/${month}`;
}

/** Shows the slots a team submitted; clicking one fills in the date/time. */
function TeamAvailabilityHint({
  name,
  slots,
  onPick,
}: {
  name: string;
  slots?: RoundScheduleSlot[];
  onPick: (datetimeLocal: string) => void;
}) {
  const list = slots ?? [];
  const byDate = new Map<string, RoundScheduleSlot[]>();
  for (const s of list) {
    byDate.set(s.date, [...(byDate.get(s.date) ?? []), s]);
  }
  return (
    <div className="rounded-md border bg-muted/40 p-2">
      <p className="text-xs font-medium">{name}</p>
      {list.length === 0 ? (
        <p className="mt-1 text-xs text-amber-600">No cargó disponibilidad</p>
      ) : (
        <div className="mt-1 space-y-1.5">
          {Array.from(byDate.entries())
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([date, daySlots]) => (
              <div key={date}>
                <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                  {new Date(date + 'T00:00:00').toLocaleDateString('es-AR', {
                    weekday: 'short',
                    day: 'numeric',
                    month: 'short',
                  })}
                </p>
                <div className="mt-0.5 flex flex-wrap gap-1">
                  {daySlots
                    .slice()
                    .sort((a, b) => a.startTime.localeCompare(b.startTime))
                    .map((s, i) => (
                      <button
                        key={`${s.startTime}-${i}`}
                        type="button"
                        onClick={() => onPick(`${s.date}T${s.startTime}`)}
                        className="rounded bg-blue-50 px-1.5 py-0.5 text-[11px] text-blue-700 hover:bg-blue-100"
                        title="Usar este horario"
                      >
                        {s.startTime}–{s.endTime}
                      </button>
                    ))}
                </div>
              </div>
            ))}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// HELPER: Detectar conflictos client-side
// ============================================================================

// ============================================================================
// Type exported for external use
// ============================================================================
export type { ProposalGridProps };
